export type AiModelOption = {
  value: string;
  label: string;
  modelName: string;
  tier: 'Free tier' | 'Pay-as-you-use tier';
  requestsPerMinute: string;
  peakActiveTokensPerMinute: string;
  monthlyInputTokenCost: string;
  monthlyCompletionTokenCost: string;
  quarterlyCost: string;
  notes?: string;
};

export const DEFAULT_AI_MODEL_VALUE = 'gpt-5-chat';

export const AI_MODEL_OPTIONS: AiModelOption[] = [
  {
    value: 'gemini-3.1-flash-lite',
    label: 'Gemini 3.1 Flash Lite — Free/Pay-as-you-use',
    modelName: 'Gemini 3.1 Flash Lite',
    tier: 'Free tier',
    requestsPerMinute: '15 free / unlimited pay-as-you-use',
    peakActiveTokensPerMinute: '120K free / 1M pay-as-you-use',
    monthlyInputTokenCost: '50M included free / $0.25 per 1M tokens',
    monthlyCompletionTokenCost: '5M included free / $1.50 per 1M tokens',
    quarterlyCost: '$0 free tier / based on token usage',
  },
  {
    value: 'gpt-5-nano',
    label: 'GPT-5 nano — Free/Pay-as-you-use',
    modelName: 'GPT-5-nano',
    tier: 'Free tier',
    requestsPerMinute: '15 free / unlimited pay-as-you-use',
    peakActiveTokensPerMinute: '120K free / 1M pay-as-you-use',
    monthlyInputTokenCost: '50M included free / $0.055 per 1M tokens',
    monthlyCompletionTokenCost: '5M included free / $0.44 per 1M tokens',
    quarterlyCost: '$0 free tier / based on token usage',
  },
  {
    value: 'gpt-4o-mini',
    label: 'GPT-4o mini — Deprecated free / Pay-as-you-use',
    modelName: 'GPT-4o mini',
    tier: 'Free tier',
    requestsPerMinute: '30 free / unlimited pay-as-you-use',
    peakActiveTokensPerMinute: '200K free / 1M pay-as-you-use',
    monthlyInputTokenCost: '500M included free / $0.165 per 1M tokens',
    monthlyCompletionTokenCost: '50M included free / $0.66 per 1M tokens',
    quarterlyCost: '$0 free tier / based on token usage',
    notes: 'Free-tier GPT-4o mini will be removed in Mar 2026.',
  },
  {
    value: 'gpt-4.1',
    label: 'GPT-4.1 — Deprecated free / Pay-as-you-use',
    modelName: 'GPT-4.1',
    tier: 'Free tier',
    requestsPerMinute: '15 free / unlimited pay-as-you-use',
    peakActiveTokensPerMinute: '120K free / 1M pay-as-you-use',
    monthlyInputTokenCost: '50M included free / $2 per 1M tokens',
    monthlyCompletionTokenCost: '5M included free / $8 per 1M tokens',
    quarterlyCost: '$0 free tier / based on token usage',
    notes: 'Deprecated. Will be removed in Mar 2026.',
  },
  {
    value: 'gpt-4o',
    label: 'GPT-4o — Deprecated',
    modelName: 'GPT-4o',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$2.75 per 1M tokens',
    monthlyCompletionTokenCost: '$11 per 1M tokens',
    quarterlyCost: 'Based on token usage',
    notes: 'Deprecated. Will be removed in Mar 2026.',
  },
  {
    value: 'gpt-4.1-mini',
    label: 'GPT-4.1 mini',
    modelName: 'GPT-4.1-mini',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$0.40 per 1M tokens',
    monthlyCompletionTokenCost: '$1.60 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'o4-mini',
    label: 'GPT-o4 mini',
    modelName: 'GPT-o4 mini',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '200K',
    monthlyInputTokenCost: '$1.10 per 1M tokens',
    monthlyCompletionTokenCost: '$4.40 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'o3',
    label: 'GPT-o3',
    modelName: 'GPT-o3',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '200K',
    monthlyInputTokenCost: '$2 per 1M tokens',
    monthlyCompletionTokenCost: '$8 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gpt-5',
    label: 'GPT-5',
    modelName: 'GPT-5',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '270K',
    monthlyInputTokenCost: '$1.375 per 1M tokens',
    monthlyCompletionTokenCost: '$11 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gpt-5-2',
    label: 'GPT-5-2',
    modelName: 'GPT-5-2',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$1.75 per 1M tokens',
    monthlyCompletionTokenCost: '$14 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gpt-5-4',
    label: 'GPT-5-4',
    modelName: 'GPT-5-4',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$2.50 per 1M tokens for prompts <270K / $5.00 per 1M tokens for prompts >270K',
    monthlyCompletionTokenCost: '$15 per 1M tokens for prompts <270K / $22.50 per 1M tokens for prompts >270K',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gpt-5-5',
    label: 'GPT-5-5',
    modelName: 'GPT-5-5',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$5.00 per 1M tokens',
    monthlyCompletionTokenCost: '$30.00 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gpt-5-4-mini',
    label: 'GPT-5-4 mini',
    modelName: 'GPT-5-4-mini',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$0.75 per 1M tokens',
    monthlyCompletionTokenCost: '$4.15 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gpt-5-4-nano',
    label: 'GPT-5-4 nano',
    modelName: 'GPT-5-4-nano',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$0.20 per 1M tokens',
    monthlyCompletionTokenCost: '$1.25 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gpt-5-mini',
    label: 'GPT-5 mini',
    modelName: 'GPT-5 mini',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$0.25 per 1M tokens',
    monthlyCompletionTokenCost: '$2 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gpt-5-chat',
    label: 'GPT-5 chat — Default',
    modelName: 'GPT-5 chat',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '120K',
    monthlyInputTokenCost: '$1.25 per 1M tokens',
    monthlyCompletionTokenCost: '$10 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro Exp',
    modelName: 'Gemini 2.5 Pro Exp',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$1.25 per 1M tokens for prompts <200K / $2.50 per 1M tokens for prompts >200K',
    monthlyCompletionTokenCost: '$10 per 1M tokens for prompts <200K / $15 per 1M tokens for prompts >200K',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gemini-2.5-flash',
    label: 'Gemini 2.5 Flash Preview',
    modelName: 'Gemini 2.5 Flash Preview',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$0.30 per 1M tokens',
    monthlyCompletionTokenCost: '$2.50 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gemini-3-flash',
    label: 'Gemini 3 Flash Preview',
    modelName: 'Gemini 3 Flash Preview',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$0.50 per 1M tokens',
    monthlyCompletionTokenCost: '$3.00 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gemini-3.1-pro',
    label: 'Gemini 3.1 Pro',
    modelName: 'Gemini 3.1 Pro',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$2.00 per 1M tokens for prompts <200K / $4.00 per 1M tokens for prompts >200K',
    monthlyCompletionTokenCost: '$12 per 1M tokens for prompts <200K / $18 per 1M tokens for prompts >200K',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gemini-2.5-pro-web-search',
    label: 'Gemini 2.5 Pro — web search',
    modelName: 'Gemini 2.5 Pro (websearch)',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$1.25 per 1M tokens for prompts <200K / $2.50 per 1M tokens for prompts >200K + $35 per 1000 requests',
    monthlyCompletionTokenCost: '$10 per 1M tokens for prompts <200K / $15 per 1M tokens for prompts >200K',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gemini-2.5-flash-web-search',
    label: 'Gemini 2.5 Flash — web search',
    modelName: 'Gemini 2.5 Flash (websearch)',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$0.30 per 1M tokens + $25 per 1000 requests',
    monthlyCompletionTokenCost: '$2.50 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gemini-3-pro-web-search',
    label: 'Gemini 3 Pro — web search',
    modelName: 'Gemini 3 Pro (websearch)',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$2.00 per 1M tokens for prompts <200K / $4.00 per 1M tokens for prompts >200K + $14 per 1000 requests',
    monthlyCompletionTokenCost: '$12 per 1M tokens for prompts <200K / $18 per 1M tokens for prompts >200K',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gemini-3.1-flash-lite-web-search',
    label: 'Gemini 3.1 Flash Lite — web search',
    modelName: 'Gemini 3.1 Flash Lite (websearch)',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$0.25 per 1M tokens + $14 per 1000 requests',
    monthlyCompletionTokenCost: '$1.50 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'gemini-3.1-pro-web-search',
    label: 'Gemini 3.1 Pro — web search',
    modelName: 'Gemini 3.1 Pro (websearch)',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$2.00 per 1M tokens for prompts <200K / $4.00 per 1M tokens for prompts >200K + $14 per 1000 requests',
    monthlyCompletionTokenCost: '$12 per 1M tokens for prompts <200K / $18 per 1M tokens for prompts >200K',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'claude-sonnet-4',
    label: 'Claude Sonnet 4',
    modelName: 'Claude Sonnet 4',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$6.00 per 1M tokens',
    monthlyCompletionTokenCost: '$22.50 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'claude-sonnet-4-5',
    label: 'Claude Sonnet 4.5',
    modelName: 'Claude Sonnet 4.5',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$6.60 per 1M tokens',
    monthlyCompletionTokenCost: '$24.75 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'claude-sonnet-4-6',
    label: 'Claude Sonnet 4.6',
    modelName: 'Claude Sonnet 4.6',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$6.00 per 1M tokens',
    monthlyCompletionTokenCost: '$22.50 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'claude-opus-4-1',
    label: 'Claude Opus 4.1',
    modelName: 'Claude Opus 4.1',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '200K',
    monthlyInputTokenCost: '$15.00 per 1M tokens',
    monthlyCompletionTokenCost: '$75.00 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'claude-opus-4-5',
    label: 'Claude Opus 4.5',
    modelName: 'Claude Opus 4.5',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '200K',
    monthlyInputTokenCost: '$5.00 per 1M tokens',
    monthlyCompletionTokenCost: '$25.00 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'claude-opus-4-6',
    label: 'Claude Opus 4.6',
    modelName: 'Claude Opus 4.6',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$5.00 per 1M tokens',
    monthlyCompletionTokenCost: '$25.00 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'claude-opus-4-8',
    label: 'Claude Opus 4.8',
    modelName: 'Claude Opus 4.8',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$5.00 per 1M tokens',
    monthlyCompletionTokenCost: '$25.00 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
  {
    value: 'claude-haiku-4-5',
    label: 'Claude Haiku 4.5',
    modelName: 'Claude Haiku 4.5',
    tier: 'Pay-as-you-use tier',
    requestsPerMinute: 'Unlimited',
    peakActiveTokensPerMinute: '1M',
    monthlyInputTokenCost: '$1.10 per 1M tokens',
    monthlyCompletionTokenCost: '$5.50 per 1M tokens',
    quarterlyCost: 'Based on token usage',
  },
];

export const AI_MODEL_SELECT_OPTIONS = AI_MODEL_OPTIONS.map((model) => ({
  label: model.label,
  value: model.value,
}));

export function getAiModelLabel(value?: string | null) {
  if (!value) return DEFAULT_AI_MODEL_VALUE;
  return AI_MODEL_OPTIONS.find((model) => model.value === value)?.label || value;
}